<template>
  <div class="keno-admin-config">
    <!-- Header -->
    <div class="config-header">
      <h2 class="config-title">{{ $t('admin.config.keno.title') }}</h2>
      <p class="config-subtitle">{{ $t('admin.config.keno.subtitle') }}</p>
    </div>

    <!-- Tab bar -->
    <div class="tab-bar" role="tablist">
      <button
        v-for="tab in tabs"
        :key="tab.id"
        class="tab-btn"
        :class="{ active: activeTab === tab.id }"
        role="tab"
        :aria-selected="activeTab === tab.id"
        @click="activeTab = tab.id"
      >
        {{ tab.label }}
      </button>
    </div>

    <!-- ─────────────── Tab: Paytable ─────────────── -->
    <div v-if="activeTab === 'paytable'" class="tab-panel">
      <div v-if="loading" class="loading-state">{{ $t('admin.config.loading') }}</div>
      <div v-else-if="loadError" class="error-banner">{{ loadError }}</div>
      <template v-else>
        <p class="section-hint">
          Edit multipliers for each (picks, hits) combination. House edge is auto-calculated
          from the hypergeometric distribution. Restart Keno game process to apply changes.
        </p>

        <!-- Pick count tabs -->
        <div class="pick-tabs">
          <button
            v-for="picks in pickCounts"
            :key="picks"
            class="pick-tab"
            :class="{ active: activePicks === picks }"
            @click="activePicks = picks"
          >
            {{ picks }} Pick{{ picks > 1 ? 's' : '' }}
          </button>
        </div>

        <!-- Paytable row editor for selected pick count -->
        <div class="paytable-card">
          <div class="paytable-card-header">
            <span class="picks-label">{{ activePicks }} Pick{{ activePicks > 1 ? 's' : '' }}</span>
            <span
              class="he-badge"
              :class="heBadgeClass(houseEdgePerPick[String(activePicks)] ?? 0)"
            >
              HE {{ (houseEdgePerPick[String(activePicks)] ?? 0).toFixed(2) }}%
            </span>
            <span class="source-badge" :class="config.source === 'database' ? 'source-db' : 'source-default'">
              {{ config.source === 'database' ? 'DB' : 'Default' }}
            </span>
          </div>

          <div class="paytable-inputs">
            <div
              v-for="hits in hitRange(activePicks)"
              :key="hits"
              class="paytable-cell"
            >
              <label class="hit-label">{{ hits }} hit{{ hits !== 1 ? 's' : '' }}</label>
              <input
                v-model.number="editablePaytable[activePicks][hits]"
                type="number"
                step="0.01"
                min="0"
                class="mult-input"
                :class="multClass(editablePaytable[activePicks][hits])"
                @change="recomputeHouseEdge(activePicks)"
              />
            </div>
          </div>
        </div>

        <!-- All picks house edge summary -->
        <div class="he-summary-grid">
          <div
            v-for="picks in pickCounts"
            :key="picks"
            class="he-summary-cell"
            :class="{ 'he-active': picks === activePicks }"
            @click="activePicks = picks"
          >
            <span class="he-picks">{{ picks }}P</span>
            <span class="he-value" :class="heBadgeClass(houseEdgePerPick[String(picks)] ?? 0)">
              {{ (houseEdgePerPick[String(picks)] ?? 0).toFixed(1) }}%
            </span>
          </div>
        </div>

        <div v-if="paytableError" class="error-banner">{{ paytableError }}</div>
        <div v-if="paytableSuccess" class="success-banner">{{ paytableSuccess }}</div>

        <div class="paytable-actions">
          <button class="btn btn-primary" :disabled="saving" @click="savePaytable">
            {{ saving ? $t('admin.config.saving') : $t('admin.config.save_paytable') }}
          </button>
          <button class="btn btn-ghost" :disabled="saving" @click="resetPaytable">
            {{ $t('admin.config.reset_to_engine_default') }}
          </button>
        </div>
      </template>
    </div>

    <!-- ─────────────── Tab: Settings ─────────────── -->
    <div v-if="activeTab === 'settings'" class="tab-panel">
      <div v-if="loading" class="loading-state">{{ $t('admin.config.loading_settings') }}</div>
      <div v-else-if="loadError" class="error-banner">{{ loadError }}</div>
      <form v-else class="settings-form" @submit.prevent="saveSettings">

        <!-- Grid / draw settings -->
        <section class="settings-section">
          <h3 class="section-title">{{ $t('admin.config.keno.section_grid_draw') }}</h3>
          <div class="form-row">
            <label class="form-label">
              {{ $t('admin.config.keno.label_grid_size') }}
              <select v-model.number="settings.grid_size" class="form-select">
                <option :value="40">40 numbers (5×8)</option>
                <option :value="80">80 numbers (8×10)</option>
              </select>
            </label>
            <label class="form-label">
              {{ $t('admin.config.keno.label_draw_count') }}
              <select v-model.number="settings.draw_count" class="form-select">
                <option :value="10">10 drawn numbers</option>
                <option :value="20">20 drawn numbers</option>
              </select>
            </label>
          </div>
          <div class="form-row">
            <label class="form-label">
              {{ $t('admin.config.keno.label_max_picks') }}
              <input
                v-model.number="settings.max_picks"
                type="number"
                min="1"
                max="10"
                step="1"
                class="form-input"
                required
              />
              <span class="form-hint">1–10 (default 10)</span>
            </label>
          </div>
        </section>

        <!-- Bet limits -->
        <section class="settings-section">
          <h3 class="section-title">{{ $t('admin.config.section_bet_limits') }}</h3>
          <div class="form-row">
            <label class="form-label">
              {{ $t('admin.config.label_min_bet') }}
              <input
                v-model.number="settings.min_bet"
                type="number"
                step="0.01"
                min="0.01"
                class="form-input"
                required
              />
            </label>
            <label class="form-label">
              {{ $t('admin.config.label_max_bet') }}
              <input
                v-model.number="settings.max_bet"
                type="number"
                step="0.01"
                min="0.01"
                class="form-input"
                required
              />
            </label>
          </div>
          <div class="form-row">
            <label class="form-label">
              {{ $t('admin.config.label_max_payout') }}
              <input
                v-model.number="settings.max_payout"
                type="number"
                step="0.01"
                min="0"
                class="form-input"
                required
              />
            </label>
          </div>
        </section>

        <!-- Speed modes -->
        <section class="settings-section">
          <h3 class="section-title">{{ $t('admin.config.keno.section_speed_modes') }}</h3>
          <div class="form-row">
            <label class="form-label">
              {{ $t('admin.config.keno.label_default_speed') }}
              <select v-model="settings.speed_mode_default" class="form-select">
                <option value="normal">Normal</option>
                <option value="turbo">Turbo (2× speed)</option>
                <option value="instant">Instant (no animation)</option>
              </select>
            </label>
          </div>
          <div class="form-row">
            <label class="toggle-label">
              <input v-model="settings.turbo_enabled" type="checkbox" class="toggle-checkbox" />
              <span>{{ $t('admin.config.keno.label_turbo_enabled') }}</span>
            </label>
            <label class="toggle-label">
              <input v-model="settings.instant_enabled" type="checkbox" class="toggle-checkbox" />
              <span>{{ $t('admin.config.keno.label_instant_enabled') }}</span>
            </label>
          </div>
        </section>

        <div v-if="settingsError" class="error-banner">{{ settingsError }}</div>
        <div v-if="settingsSuccess" class="success-banner">{{ settingsSuccess }}</div>

        <div class="form-actions">
          <button type="submit" class="btn btn-primary" :disabled="saving">
            {{ saving ? $t('admin.config.saving') : $t('admin.config.save_settings') }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import apiClient from '@/api/client'

interface KenoConfigResponse {
  source: 'database' | 'default'
  grid_size: number
  draw_count: number
  max_picks: number
  min_bet: string
  max_bet: string
  max_payout: string
  speed_mode_default: 'normal' | 'turbo' | 'instant'
  turbo_enabled: boolean
  instant_enabled: boolean
  paytable: Record<string, number[]>
  house_edge_per_pick: Record<string, number>
}

const { t } = useI18n()

type TabId = 'paytable' | 'settings'

const tabs = computed(() => [
  { id: 'paytable' as TabId, label: t('admin.config.keno.tab_paytable') },
  { id: 'settings' as TabId, label: t('admin.config.keno.tab_settings') },
])

const activeTab = ref<TabId>('paytable')
const activePicks = ref(1)

const loading = ref(true)
const saving = ref(false)
const loadError = ref('')
const paytableError = ref('')
const paytableSuccess = ref('')
const settingsError = ref('')
const settingsSuccess = ref('')

// Full config from server (used to determine source badge)
const config = ref<Pick<KenoConfigResponse, 'source'>>({ source: 'default' })

// Editable house edge values (re-computed locally on input change)
const houseEdgePerPick = ref<Record<string, number>>({})

// Editable paytable: [picks][hits] => multiplier
// Picks 1-10, hits 0-picks
const editablePaytable = reactive<Record<number, Record<number, number>>>({})

// Editable settings
const settings = reactive({
  grid_size: 80,
  draw_count: 20,
  max_picks: 10,
  min_bet: '1.00',
  max_bet: '10000.00',
  max_payout: '0.00',
  speed_mode_default: 'normal' as 'normal' | 'turbo' | 'instant',
  turbo_enabled: true,
  instant_enabled: true,
})

const pickCounts = computed(() => Array.from({ length: settings.max_picks }, (_, i) => i + 1))

function hitRange(picks: number): number[] {
  return Array.from({ length: picks + 1 }, (_, i) => i)
}

function heBadgeClass(he: number): string {
  if (he <= 3) return 'he-green'
  if (he <= 7) return 'he-yellow'
  return 'he-red'
}

function multClass(mult: number): string {
  if (mult >= 100) return 'mult-jackpot'
  if (mult >= 10) return 'mult-high'
  if (mult > 0) return 'mult-mid'
  return 'mult-zero'
}

/** Build empty paytable rows for picks 1..maxPicks */
function initPaytable(maxPicks: number) {
  for (let picks = 1; picks <= maxPicks; picks++) {
    if (!editablePaytable[picks]) {
      editablePaytable[picks] = {}
      for (let hits = 0; hits <= picks; hits++) {
        editablePaytable[picks][hits] = 0
      }
    }
  }
}

/** Populate editable paytable from API response */
function populatePaytable(paytable: Record<string, number[]>) {
  for (const [picksStr, multipliers] of Object.entries(paytable)) {
    const picks = parseInt(picksStr, 10)
    editablePaytable[picks] = {}
    multipliers.forEach((mult, hits) => {
      editablePaytable[picks][hits] = mult
    })
  }
}

/** Estimate house edge for a pick count from current editable values.
 *  Uses a simplified hypergeometric EV — full calc is done server-side on save.
 *  We display an approximation here for immediate feedback.
 */
function recomputeHouseEdge(picks: number) {
  const row = editablePaytable[picks]
  if (!row) return

  const gridSize = settings.grid_size
  const drawCount = settings.draw_count

  // Compute hypergeometric probabilities
  let ev = 0
  for (let hits = 0; hits <= picks; hits++) {
    const prob = hypergeometric(gridSize, drawCount, picks, hits)
    ev += prob * (row[hits] ?? 0)
  }

  const rtp = ev * 100
  houseEdgePerPick.value[String(picks)] = Math.max(0, parseFloat((100 - rtp).toFixed(2)))
}

/** Recompute house edge for all pick counts */
function recomputeAllHouseEdges() {
  for (let picks = 1; picks <= settings.max_picks; picks++) {
    recomputeHouseEdge(picks)
  }
}

/** Hypergeometric probability P(X=hits) using log-factorial for overflow safety */
function hypergeometric(n: number, k: number, picks: number, hits: number): number {
  if (hits < 0 || hits > picks || hits > k) return 0
  if (k - hits > n - picks) return 0

  const logP =
    logCombination(picks, hits) +
    logCombination(n - picks, k - hits) -
    logCombination(n, k)

  return Math.exp(logP)
}

function logCombination(n: number, k: number): number {
  if (k < 0 || k > n) return -Infinity
  if (k === 0 || k === n) return 0
  return logFactorial(n) - logFactorial(k) - logFactorial(n - k)
}

function logFactorial(n: number): number {
  if (n <= 1) return 0
  let sum = 0
  for (let i = 2; i <= n; i++) sum += Math.log(i)
  return sum
}

async function loadConfig() {
  loading.value = true
  loadError.value = ''
  try {
    const res = await apiClient.get<KenoConfigResponse>('/admin/api/games/keno/config')
    const data = res.data
    config.value = { source: data.source }

    // Settings
    settings.grid_size = data.grid_size
    settings.draw_count = data.draw_count
    settings.max_picks = data.max_picks
    settings.min_bet = data.min_bet
    settings.max_bet = data.max_bet
    settings.max_payout = data.max_payout
    settings.speed_mode_default = data.speed_mode_default
    settings.turbo_enabled = data.turbo_enabled
    settings.instant_enabled = data.instant_enabled

    // Paytable
    initPaytable(data.max_picks)
    populatePaytable(data.paytable)

    // House edge from server
    houseEdgePerPick.value = { ...data.house_edge_per_pick }
  } catch {
    loadError.value = t('admin.config.keno.error_load')
  } finally {
    loading.value = false
  }
}

async function savePaytable() {
  paytableError.value = ''
  paytableSuccess.value = ''
  saving.value = true

  // Build paytable payload
  const paytablePayload: Record<string, number[]> = {}
  for (let picks = 1; picks <= settings.max_picks; picks++) {
    paytablePayload[String(picks)] = hitRange(picks).map((hits) => editablePaytable[picks]?.[hits] ?? 0)
  }

  try {
    const res = await apiClient.post<KenoConfigResponse>('/admin/api/games/keno/config', {
      grid_size: settings.grid_size,
      draw_count: settings.draw_count,
      max_picks: settings.max_picks,
      min_bet: settings.min_bet,
      max_bet: settings.max_bet,
      max_payout: settings.max_payout,
      speed_mode_default: settings.speed_mode_default,
      turbo_enabled: settings.turbo_enabled,
      instant_enabled: settings.instant_enabled,
      paytable: paytablePayload,
    })
    config.value = { source: res.data.source }
    houseEdgePerPick.value = { ...res.data.house_edge_per_pick }
    paytableSuccess.value = t('admin.config.keno.paytable_save_success')
  } catch (err: unknown) {
    const e = err as { response?: { data?: { message?: string } } }
    paytableError.value = e.response?.data?.message ?? t('admin.config.keno.paytable_error_save')
  } finally {
    saving.value = false
  }
}

async function resetPaytable() {
  paytableError.value = ''
  paytableSuccess.value = ''
  saving.value = true

  try {
    // Send null paytable to reset to engine default
    const res = await apiClient.post<KenoConfigResponse>('/admin/api/games/keno/config', {
      grid_size: settings.grid_size,
      draw_count: settings.draw_count,
      max_picks: settings.max_picks,
      min_bet: settings.min_bet,
      max_bet: settings.max_bet,
      max_payout: settings.max_payout,
      speed_mode_default: settings.speed_mode_default,
      turbo_enabled: settings.turbo_enabled,
      instant_enabled: settings.instant_enabled,
      paytable: null,
    })
    config.value = { source: res.data.source }
    houseEdgePerPick.value = { ...res.data.house_edge_per_pick }
    populatePaytable(res.data.paytable)
    paytableSuccess.value = t('admin.config.keno.paytable_save_success')
  } catch (err: unknown) {
    const e = err as { response?: { data?: { message?: string } } }
    paytableError.value = e.response?.data?.message ?? t('admin.config.keno.paytable_error_save')
  } finally {
    saving.value = false
  }
}

async function saveSettings() {
  settingsError.value = ''
  settingsSuccess.value = ''
  saving.value = true

  try {
    // Ensure paytable rows match the current max_picks
    const paytablePayload: Record<string, number[]> = {}
    for (let picks = 1; picks <= settings.max_picks; picks++) {
      paytablePayload[String(picks)] = hitRange(picks).map((hits) => editablePaytable[picks]?.[hits] ?? 0)
    }

    const res = await apiClient.post<KenoConfigResponse>('/admin/api/games/keno/config', {
      grid_size: settings.grid_size,
      draw_count: settings.draw_count,
      max_picks: settings.max_picks,
      min_bet: settings.min_bet,
      max_bet: settings.max_bet,
      max_payout: settings.max_payout,
      speed_mode_default: settings.speed_mode_default,
      turbo_enabled: settings.turbo_enabled,
      instant_enabled: settings.instant_enabled,
      paytable: paytablePayload,
    })
    config.value = { source: res.data.source }
    houseEdgePerPick.value = { ...res.data.house_edge_per_pick }
    settingsSuccess.value = t('admin.config.keno.settings_save_success')
  } catch (err: unknown) {
    const e = err as { response?: { data?: { message?: string } } }
    settingsError.value = e.response?.data?.message ?? t('admin.config.keno.settings_error_save')
  } finally {
    saving.value = false
  }
}

onMounted(async () => {
  await loadConfig()
  recomputeAllHouseEdges()
})
</script>

<style scoped>
.keno-admin-config {
  padding: 0;
}

.config-header {
  margin-bottom: 24px;
}

.config-title {
  font-size: 1.375rem;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0 0 6px;
}

.config-subtitle {
  color: var(--text-secondary, #b1bad3);
  font-size: 0.875rem;
  margin: 0;
}

/* Tab bar */
.tab-bar {
  display: flex;
  gap: 4px;
  margin-bottom: 24px;
  border-bottom: 1px solid var(--border, #2d4a5a);
  padding-bottom: 0;
}

.tab-btn {
  padding: 8px 18px;
  border: none;
  background: transparent;
  color: var(--text-secondary, #b1bad3);
  font-size: 0.875rem;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  transition: all 0.15s;
}

.tab-btn.active {
  color: var(--accent, #1a9e6e);
  border-bottom-color: var(--accent, #1a9e6e);
}

.tab-btn:hover:not(.active) {
  color: var(--text-primary, #fff);
}

.tab-panel {
  padding: 4px 0 0;
}

/* Loading / error / success */
.loading-state {
  color: var(--text-secondary, #b1bad3);
  padding: 32px;
  text-align: center;
}

.error-banner {
  background: rgba(239, 68, 68, 0.12);
  border: 1px solid rgba(239, 68, 68, 0.4);
  border-radius: 8px;
  color: var(--color-danger-light, #f87171);
  padding: 12px 16px;
  font-size: 0.875rem;
  margin-bottom: 16px;
}

.success-banner {
  background: rgba(26, 158, 110, 0.12);
  border: 1px solid rgba(26, 158, 110, 0.4);
  border-radius: 8px;
  color: var(--color-success-light, #34d399);
  padding: 12px 16px;
  font-size: 0.875rem;
  margin-bottom: 16px;
}

.section-hint {
  color: var(--text-secondary, #b1bad3);
  font-size: 0.8rem;
  margin: 0 0 20px;
}

/* Pick count tabs */
.pick-tabs {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

.pick-tab {
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid var(--border, #2d4a5a);
  background: var(--bg-secondary, #213743);
  color: var(--text-secondary, #b1bad3);
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.15s;
}

.pick-tab.active {
  background: var(--accent, #1a9e6e);
  border-color: var(--accent, #1a9e6e);
  color: var(--color-on-dark, #fff);
}

/* Paytable card */
.paytable-card {
  background: var(--bg-secondary, #213743);
  border: 1px solid var(--border, #2d4a5a);
  border-radius: 10px;
  padding: 16px 20px;
  margin-bottom: 20px;
}

.paytable-card-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
}

.picks-label {
  font-weight: 600;
  color: var(--text-primary, #fff);
  font-size: 0.9rem;
}

.he-badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 12px;
}

.he-green {
  background: rgba(26, 158, 110, 0.2);
  color: var(--color-success-light, #34d399);
}

.he-yellow {
  background: rgba(234, 179, 8, 0.2);
  color: var(--color-warning-light, #fbbf24);
}

.he-red {
  background: rgba(239, 68, 68, 0.2);
  color: var(--color-danger-light, #f87171);
}

.source-badge {
  font-size: 0.7rem;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 600;
  margin-left: auto;
}

.source-db {
  background: rgba(59, 130, 246, 0.2);
  color: var(--color-info-light, #60a5fa);
}

.source-default {
  background: rgba(107, 114, 128, 0.2);
  color: var(--color-muted, #9ca3af);
}

.paytable-inputs {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.paytable-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  min-width: 70px;
}

.hit-label {
  font-size: 0.72rem;
  color: var(--text-secondary, #b1bad3);
  white-space: nowrap;
}

.mult-input {
  width: 70px;
  padding: 6px 8px;
  background: var(--bg-primary, #0f212e);
  border: 1px solid var(--border, #2d4a5a);
  border-radius: 6px;
  color: var(--text-primary, #fff);
  font-size: 0.875rem;
  text-align: right;
}

.mult-input.mult-jackpot { border-color: var(--color-warning-light, #fbbf24); }
.mult-input.mult-high    { border-color: var(--color-success-light, #34d399); }
.mult-input.mult-mid     { border-color: var(--color-info-light, #60a5fa); }
.mult-input.mult-zero    { opacity: 0.5; }

/* House edge summary grid */
.he-summary-grid {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

.he-summary-cell {
  background: var(--bg-secondary, #213743);
  border: 1px solid var(--border, #2d4a5a);
  border-radius: 8px;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  cursor: pointer;
  transition: border-color 0.15s;
  min-width: 54px;
}

.he-summary-cell.he-active {
  border-color: var(--accent, #1a9e6e);
}

.he-picks {
  font-size: 0.75rem;
  color: var(--text-secondary, #b1bad3);
  font-weight: 600;
}

.he-value {
  font-size: 0.75rem;
  font-weight: 600;
}

.paytable-actions {
  display: flex;
  gap: 10px;
  margin-top: 8px;
}

/* Settings form */
.settings-form {
  max-width: 640px;
}

.settings-section {
  background: var(--bg-secondary, #213743);
  border: 1px solid var(--border, #2d4a5a);
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 20px;
}

.section-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-primary, #fff);
  margin: 0 0 16px;
}

.form-row {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}

.form-row:last-child {
  margin-bottom: 0;
}

.form-label {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 0.8rem;
  color: var(--text-secondary, #b1bad3);
  flex: 1;
  min-width: 180px;
}

.form-input,
.form-select {
  padding: 8px 12px;
  background: var(--bg-primary, #0f212e);
  border: 1px solid var(--border, #2d4a5a);
  border-radius: 6px;
  color: var(--text-primary, #fff);
  font-size: 0.875rem;
}

.form-hint {
  font-size: 0.72rem;
  color: var(--text-secondary, #b1bad3);
  opacity: 0.7;
}

.toggle-label {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.875rem;
  color: var(--text-primary, #fff);
  cursor: pointer;
  flex: 1;
  min-width: 200px;
}

.toggle-checkbox {
  width: 16px;
  height: 16px;
  accent-color: var(--accent, #1a9e6e);
}

.form-actions {
  display: flex;
  gap: 10px;
  margin-top: 8px;
}

/* Buttons */
.btn {
  padding: 9px 20px;
  border-radius: 7px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: opacity 0.15s;
}

.btn:disabled {
  opacity: 0.55;
  cursor: default;
}

.btn-primary {
  background: var(--accent, #1a9e6e);
  color: var(--color-on-dark, #fff);
}

.btn-ghost {
  background: transparent;
  border: 1px solid var(--border, #2d4a5a);
  color: var(--text-secondary, #b1bad3);
}

.btn-ghost:hover:not(:disabled) {
  border-color: var(--text-secondary, #b1bad3);
  color: var(--text-primary, #fff);
}
</style>
